import { useEffect, useState } from "react";

// Live weather for Accra from Open-Meteo (free, no key, allows browser requests).
const URL =
  "https://api.open-meteo.com/v1/forecast?latitude=5.6037&longitude=-0.187" +
  "&current=temperature_2m,weather_code,is_day" +
  "&hourly=temperature_2m,weather_code,is_day" +
  "&daily=temperature_2m_max,temperature_2m_min,sunrise,sunset" +
  "&timezone=Africa%2FAccra&forecast_days=2";

// WMO weather codes -> what to call it and which glyph to draw.
// `sky` picks the widget's background, as Apple's Weather widget tints itself by the conditions.
export const describeWeather = (code, isDay) => {
  const night = !isDay;
  const out = (text, icon, sky) => ({ text, icon, sky: night ? "night" : sky });
  if (code === 0) return out(isDay ? "Sunny" : "Clear", isDay ? "sun" : "moon", "clear");
  if (code === 1) return out(isDay ? "Mostly Sunny" : "Mostly Clear", isDay ? "cloudSun" : "cloudMoon", "clear");
  if (code === 2) return out("Partly Cloudy", isDay ? "cloudSun" : "cloudMoon", "cloudy");
  if (code === 3) return out("Cloudy", "cloud", "cloudy");
  if (code === 45 || code === 48) return out("Foggy", "fog", "cloudy");
  if (code >= 51 && code <= 57) return out("Drizzle", "drizzle", "rain");
  if (code === 65 || code === 82) return out("Heavy Rain", "heavyRain", "rain");
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return out("Rain", "rain", "rain");
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return out("Snow", "snow", "cloudy");
  if (code >= 95) return out("Thunderstorms", "storm", "rain");
  return out("Cloudy", "cloud", "cloudy");
};

// "2026-10-25T13:00" -> "1PM"; with minutes -> "5:42PM"
const clockLabel = (iso, withMinutes) => {
  const h = Number(iso.slice(11, 13));
  const m = iso.slice(14, 16);
  return `${h % 12 || 12}${withMinutes ? `:${m}` : ""}${h < 12 ? "AM" : "PM"}`;
};

// One request per page load, shared by every caller.
let request = null;

const load = () => {
  if (!request) {
    request = fetch(URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        // The next six hours after the current one, with sunrise or sunset slotted in where
        // it falls, as Apple's widget does.
        const hourNow = d.current.time.slice(0, 13);
        const start = d.hourly.time.findIndex((t) => t.startsWith(hourNow)) + 1;
        const tempAt = (iso) => {
          const i = d.hourly.time.findIndex((t) => t.startsWith(iso.slice(0, 13)));
          return Math.round(d.hourly.temperature_2m[i >= 0 ? i : start]);
        };
        const hours = d.hourly.time.slice(start, start + 6).map((t, i) => ({
          time: t,
          label: clockLabel(t),
          temp: Math.round(d.hourly.temperature_2m[start + i]),
          ...describeWeather(d.hourly.weather_code[start + i], d.hourly.is_day[start + i]),
        }));
        const last = hours.length ? hours[hours.length - 1].time : d.current.time;
        const events = [
          ...d.daily.sunrise.map((t) => ({ time: t, text: "Sunrise", icon: "sunrise" })),
          ...d.daily.sunset.map((t) => ({ time: t, text: "Sunset", icon: "sunset" })),
        ]
          .filter((e) => e.time > d.current.time && e.time <= last)
          .map((e) => ({ ...e, label: clockLabel(e.time, true), temp: tempAt(e.time) }));
        const hourly = [...hours, ...events].sort((a, b) => (a.time < b.time ? -1 : 1)).slice(0, 6);
        return {
          temp: Math.round(d.current.temperature_2m),
          high: Math.round(d.daily.temperature_2m_max[0]),
          low: Math.round(d.daily.temperature_2m_min[0]),
          ...describeWeather(d.current.weather_code, d.current.is_day),
          hourly,
        };
      })
      .catch(() => {
        request = null; // let a later open try again
        return null;
      });
  }
  return request;
};

// { status: "loading" | "ready" | "error", weather }
const useWeather = (enabled) => {
  const [result, setResult] = useState({ status: "loading", weather: null });

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    load().then((weather) => {
      if (!cancelled) setResult({ status: weather ? "ready" : "error", weather });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return result;
};

export default useWeather;
